import { Checkbox, Grid } from "@mui/material";
import { useFormik } from "formik";
import { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import { AsyncSelectValue } from "../../../../../../../../components/AsyncSelect";
import AsyncSelectScroll from "../../../../../../../../components/AsyncSelectScroll";
import CustomSelect from "../../../../../../../../components/CustomSelect";
import { FlexCenterWrapper } from "../../../../../../../../components/DefaultNoDataWrapper";
import Flex from "../../../../../../../../components/Flex";
import Text from "../../../../../../../../components/Text";
import StyledButtonV2 from "../../../../../../../../components/app/StyledButtonV2";
import StyledFormModal from "../../../../../../../../components/globalModal/StyledModal";
import { useDebounce } from "../../../../../../../../hooks/use-debounce";
import { DisclosureHost } from "../../../../../../../../hooks/use-disclosure";
import { useVisitNoteTemplateGetAll } from "../../../../../../../../queries/practice-setting/template/use-visit-note-template-get-all";
import { GetAllVisitNoteTemplateTemplateTypeEnum } from "../../../../../../../../services";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  getXtenantId,
} from "../../../../../../../../utils/utils";
import {
  VisitNoteFormValues,
  visitNoteTypeOptions,
  visitNoteinitialValues,
} from "../../../../../settings/practice-setting/visit-note-template/VisitNoteTemplateForm";

interface VisitNoteTemplatePreviewProps {
  disclosureHost: DisclosureHost;
  onExport: (selectedNotes: any) => void;
}

const templateSections = [
  "subjectiveFreeNote",
  "allergyFreeText",
  "objectiveFreeNote",
  "hpi",
  "problems",
  "habits",
  "assessmentFreeNote",
  "planFreeNote",
  "carePlan",
  "instruction",
  "followUp",
];

const VisitNoteTemplatePreview = ({
  disclosureHost,
  onExport,
}: VisitNoteTemplatePreviewProps) => {
  type FormValues = {
    visitNoteType: GetAllVisitNoteTemplateTemplateTypeEnum;
    searchTemplate: string;
    template: AsyncSelectValue | null;
  };

  const validationSchema: Yup.ObjectSchema<any> = Yup.object().shape({
    visitNoteType: Yup.string().required("Visit Note Type is required"),
    template: Yup.object().nullable(),
  });
  const initialValues: FormValues = {
    visitNoteType: "SIMPLE_SOAP_NOTE",
    searchTemplate: "",
    template: null,
  };
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: () => {},
  });
  const [selectedNoteSections, setSelectedNoteSections] = useState<
    VisitNoteFormValues | undefined
  >();
  const [searchKey, setSearchKey] = useDebounce<string>("");
  const [curSelectedTemplate, setCurSelectedTemplate] = useState<any>(null);
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);

  const { data: templateData, isFetching } = useVisitNoteTemplateGetAll({
    pagination,
    archive: false,
    searchString: searchKey,
    templateType: formik.values
      .visitNoteType as GetAllVisitNoteTemplateTemplateTypeEnum,
    xTENANTID: getXtenantId(),
  });

  const templatesById = useRef<Record<string, any>>({});
  useEffect(() => {
    templateData?.data?.content?.forEach((it: any) => {
      templatesById.current[it.uuid] = it;
    });
  }, [templateData?.data?.content]);

  function handleClose() {
    disclosureHost.close();
  }

  const PreviewNote = ({
    data,
    selectedSections,
    setSelectedSections,
  }: {
    data: any;
    selectedSections: any;
    setSelectedSections: (sections: any) => void;
  }) => {
    const templateData = { ...visitNoteinitialValues, ...data };

    const availableItems = templateSections.filter(
      (item) => templateData?.[item]
    );

    const allSelected = availableItems.every(
      (item) => !!selectedSections?.[item]
    );

    return data ? (
      <Flex
        height={"calc(100vh - 380px)"}
        overflow={"scroll"}
        flexDirection={"column"}
        gap={0.4}
        width={"100%"}
      >
        <Flex gap={2} justifyContent="flex-start">
          <StyledButtonV2
            variant="outlined"
            size="small"
            onClick={() => {
              setSelectedSections?.((prev: any) => {
                const newSections = { ...prev };
                if (allSelected) {
                  availableItems.forEach((item) => {
                    newSections[item] = "";
                  });
                } else {
                  availableItems.forEach((item) => {
                    newSections[item] = templateData?.[item] || "";
                  });
                }
                return newSections;
              });
            }}
            label={allSelected ? "Clear All" : "Select All"}
          />
        </Flex>

        <Grid container spacing={2} mt={1.4}>
          {templateSections.map(
            (item) =>
              templateData?.[item] && (
                <Grid item xs={12} md={6} key={item}>
                  <Flex gap={0.5} height={"20px"} alignItems={"center"}>
                    <Checkbox
                      checked={!!selectedSections?.[item]}
                      onChange={(e) => {
                        setSelectedSections?.((prev: any) => {
                          return {
                            ...prev,
                            [item]: e.target.checked
                              ? templateData?.[item]
                              : "",
                          };
                        });
                      }}
                    />
                    <Text>{item.replace("FreeNote", "")}</Text>
                  </Flex>
                </Grid>
              )
          )}
        </Grid>
        {/* {formMapping[data?.templateType]} */}
      </Flex>
    ) : (
      <FlexCenterWrapper>Select Template To Preview</FlexCenterWrapper>
    );
  };

  return (
    <StyledFormModal
      open={disclosureHost.opened}
      onClose={handleClose}
      dialogTitle="Visit Note Template Preview"
      dialogAction={
        <StyledButtonV2
          disabled={!selectedNoteSections}
          onClick={() => {
            onExport(selectedNoteSections as any);
            disclosureHost.close();
          }}
          label={"Export"}
        />
      }
      sx={{
        "& .MuiDialog-paper": {
          width: "38vw",
          height: "70vh",
        },
      }}
    >
      <Flex gap={2} width={"100%"} flexDirection={"column"} pt={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <CustomSelect
              required
              label="Visit Note Type"
              options={visitNoteTypeOptions}
              value={formik.values.visitNoteType}
              error={
                formik.touched.visitNoteType && formik.errors.visitNoteType
              }
              onChange={(value) => {
                formik.setFieldValue("visitNoteType", value as any);
                setSelectedNoteSections(undefined);
                setCurSelectedTemplate(null);
                formik.setFieldValue("template", undefined);
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <AsyncSelectScroll
              required
              label="Template"
              options={templateData?.data?.content?.map((it: any) => ({
                value: it.uuid,
                label: it.templateName,
              }))}
              onSearch={(v) => {
                setSearchKey(v);
                setPagination((p) => ({ ...p, page: 0 }));
              }}
              value={formik.values.template}
              pagination={pagination}
              setPagination={setPagination}
              totalElements={templateData?.data?.page?.totalElements}
              loading={isFetching}
              onChange={(value) => {
                formik.setFieldValue("template", value as any);
                const curTemplate = templatesById.current[value?.value ?? ""];
                setCurSelectedTemplate(curTemplate);
                setSelectedNoteSections(curTemplate);
              }}
            />
          </Grid>
        </Grid>

        <PreviewNote
          data={curSelectedTemplate}
          selectedSections={selectedNoteSections}
          setSelectedSections={setSelectedNoteSections}
        />
      </Flex>
    </StyledFormModal>
  );
};

export default VisitNoteTemplatePreview;
